import { useEffect, useState, useCallback } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "pwa_install_dismissed_at";
const IOS_DISMISS_KEY = "pwa_ios_install_banner_dismissed_at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isDismissed(key) {
  try {
    const ts = parseInt(localStorage.getItem(key) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * "Install app" button.
 *
 * Renders only when:
 *   - The browser fired beforeinstallprompt at least once (Chrome/Edge/Android),
 *   - The app is not already installed (display-mode standalone),
 *   - The user hasn't dismissed the prompt in the last 14 days.
 */
export default function InstallAppButton({ variant = "header" }) {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed(DISMISS_KEY)) return;
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome !== "accepted") {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
      }
    } catch {}
    setDeferred(null);
    setVisible(false);
  }, [deferred]);

  if (!visible || !deferred) return null;

  if (variant === "login") {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn-login"
        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#1e2a78] hover:text-[#0F1E3D] hover:underline">
        <Download className="h-4 w-4" /> Install app
      </button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={handleClick}
      data-testid="install-app-btn"
      className="hidden sm:inline-flex items-center gap-1.5 border-[#1e2a78]/30 text-[#1e2a78] hover:bg-[#1e2a78]/5">
      <Download className="h-4 w-4" />
      Install app
    </Button>
  );
}

/**
 * iOS Safari install hint.
 *
 * iOS Safari never fires beforeinstallprompt, so we show a small dismissible
 * banner with the Share → Add to Home Screen instructions. Only rendered on
 * iOS Safari and only when the app isn't already running as a standalone PWA.
 */
export function IOSInstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOS()) return;
    if (isStandalone()) return;
    if (isDismissed(IOS_DISMISS_KEY)) return;
    setShow(true);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(IOS_DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="ios-install-banner"
      className="mb-4 flex items-start gap-3 rounded-md border border-[#1e2a78]/15 bg-[#1e2a78]/5 px-3 py-2.5 text-xs text-[#0F1E3D]">
      <Share className="mt-0.5 h-4 w-4 shrink-0 text-[#1e2a78]" />
      <div className="flex-1 leading-snug">
        <span className="font-semibold">Install ExpenseReconcile:</span>{" "}
        tap <span className="font-medium">Share</span> →{" "}
        <span className="font-medium">Add to Home Screen</span>.
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="ios-install-banner-dismiss"
        className="text-slate-400 hover:text-slate-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
